import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PlatformErrorBoundary } from '../PlatformErrorBoundary';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { useAgentApps } from '../../hooks/useAgentApps';
import { getChatSettings, saveChatSettings } from '../../lib/companyProfile';
import { ROLE_LABELS } from '../../lib/userRoleHierarchy';
import { getPublicChatEmbedOrigin } from '../../lib/domainRouting';
import { buildChatWidgetEmbedSnippet } from '../../lib/chatWidgetTenant';

const DEFAULT_SETTINGS = {
  chatEnabled: false,
  primaryBrandColor: '#D85A30',
  welcomeMessage: '',
  webhookUrl: '',
  assignedAgents: [],
  quickReplies: [],
};

const ROUTABLE_ROLES = new Set(['agent', 'manager', 'company_admin']);

function userLabel(user) {
  return (
    user?.displayName ||
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    user?.uid ||
    '?'
  );
}

/**
 * Company admin chat widget configuration.
 * Enablement is stored as companies/{id}.activeModules.chatWidget.
 */
export default function ChatSettings() {
  const { companyId, refresh } = useWorkspace();
  const { users: companyUsers, loading: usersLoading } = useCompanyUsers(companyId);
  // Chat-enabled apps for this company (admin view bypasses the assignment filter).
  const { apps: chatApps } = useAgentApps('chat', { companyId, role: 'company_admin' });
  const [embedAppId, setEmbedAppId] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const embedOrigin = useMemo(() => getPublicChatEmbedOrigin(), []);
  const widgetInstallSnippet = useMemo(() => {
    if (!companyId) return '';
    return buildChatWidgetEmbedSnippet(embedOrigin, companyId, embedAppId);
  }, [embedOrigin, companyId, embedAppId]);

  const routableUsers = useMemo(
    () =>
      companyUsers.filter((u) => ROUTABLE_ROLES.has(u.role)).sort((a, b) =>
        userLabel(a).localeCompare(userLabel(b)),
      ),
    [companyUsers],
  );

  const selectedAgents = useMemo(() => {
    const ids = new Set(settings.assignedAgents || []);
    return routableUsers.filter((u) => ids.has(u.uid));
  }, [routableUsers, settings.assignedAgents]);

  useEffect(() => {
    if (!companyId) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const fetchedSettings = await getChatSettings(companyId);
        if (!cancelled && fetchedSettings) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...fetchedSettings,
            assignedAgents: Array.isArray(fetchedSettings.assignedAgents)
              ? fetchedSettings.assignedAgents
              : [],
            quickReplies: Array.isArray(fetchedSettings.quickReplies)
              ? fetchedSettings.quickReplies
              : [],
          });
        }
      } catch (error) {
        console.error('Error fetching chat settings:', error);
        if (!cancelled) {
          setSnackbarMessage('Failed to fetch chat settings.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId) {
      setSnackbarMessage('No company linked to this workspace.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setIsSaving(true);
    try {
      await saveChatSettings(companyId, settings);
      await refresh?.();
      const agentCount = (settings.assignedAgents || []).length;
      setSnackbarMessage(
        settings.chatEnabled
          ? `Saved. Chat enabled; ${agentCount} agent(s) assigned for live alerts.`
          : `Saved. Chat disabled; ${agentCount} agent(s) kept for routing.`,
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving chat settings:', error);
      setSnackbarMessage(error.message || 'Failed to save chat settings.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAgentsChange = (_event, selected) => {
    setSettings((prev) => ({
      ...prev,
      assignedAgents: (selected || []).map((u) => u.uid).filter(Boolean),
    }));
  };

  const handleQuickReplyChange = (index, value) => {
    setSettings((prev) => {
      const next = [...(prev.quickReplies || [])];
      next[index] = value;
      return { ...prev, quickReplies: next };
    });
  };

  const handleAddQuickReply = () => {
    setSettings((prev) => ({
      ...prev,
      quickReplies: [...(prev.quickReplies || []), ''],
    }));
  };

  const handleRemoveQuickReply = (index) => {
    setSettings((prev) => ({
      ...prev,
      quickReplies: (prev.quickReplies || []).filter((_, i) => i !== index),
    }));
  };

  const handleCopyEmbed = async () => {
    if (!widgetInstallSnippet) return;
    try {
      await navigator.clipboard.writeText(widgetInstallSnippet);
      setSnackbarMessage('Embed code copied to clipboard.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Copy embed snippet failed:', err);
      setSnackbarMessage('Could not copy. Select the code block and copy manually.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PlatformErrorBoundary>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Chat Widget Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Enablement is stored as <code>activeModules.chatWidget</code>. Routing agents are
          saved under <code>chatSettings.assignedAgents</code>.
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.chatEnabled === true}
                onChange={handleChange}
                name="chatEnabled"
                color="primary"
              />
            }
            label="Enable Chat Widget"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0 }}>
            Maps to <strong>activeModules.chatWidget</strong>
            {settings.chatEnabled ? ' (on)' : ' (off)'}.
          </Typography>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Chat Routing & Assignment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select agents, managers, and company admins who should receive live chat alerts
            when a visitor starts a conversation.
          </Typography>
          {usersLoading ? (
            <CircularProgress size={24} />
          ) : routableUsers.length === 0 ? (
            <Alert severity="info">
              No agents, managers, or company admins found for this company yet.
            </Alert>
          ) : (
            <Autocomplete
              multiple
              options={routableUsers}
              value={selectedAgents}
              onChange={handleAgentsChange}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${userLabel(option)} (${ROLE_LABELS[option.role] || option.role})`
              }
              isOptionEqualToValue={(a, b) => a.uid === b.uid}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.uid}
                    size="small"
                    label={userLabel(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned agents"
                  placeholder="Select users…"
                  helperText="Stored as chatSettings.assignedAgents on the company document."
                />
              )}
            />
          )}
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Replies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pre-written responses agents can send with one click in Chat Operations.
            Stored as <code>chatSettings.quickReplies</code>.
          </Typography>
          <Stack spacing={1.5}>
            {(settings.quickReplies || []).map((reply, index) => (
              <Stack key={`qr-${index}`} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  label={`Quick reply ${index + 1}`}
                  value={reply}
                  onChange={(e) => handleQuickReplyChange(index, e.target.value)}
                  multiline
                  maxRows={3}
                />
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={() => handleRemoveQuickReply(index)}
                  sx={{ flexShrink: 0, mt: 0.5 }}
                >
                  Remove
                </Button>
              </Stack>
            ))}
            <Button variant="outlined" onClick={handleAddQuickReply} sx={{ alignSelf: 'flex-start' }}>
              Add quick reply
            </Button>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Customization
          </Typography>
          <TextField
            label="Primary Brand Color (Hex)"
            type="color"
            name="primaryBrandColor"
            value={settings.primaryBrandColor || '#D85A30'}
            onChange={handleChange}
            fullWidth
            margin="normal"
            sx={{ width: 100 }}
          />
          <TextField
            label="Welcome Message"
            name="welcomeMessage"
            value={settings.welcomeMessage}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Webhook URL"
            name="webhookUrl"
            value={settings.webhookUrl}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
        </Paper>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                Widget Installation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paste this iframe on your public website. The <code>companyId</code> query
                parameter scopes the widget to your tenant ({companyId || '—'}); adding an
                app scopes chat sessions to that app/brand.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyEmbed}
              disabled={!widgetInstallSnippet}
            >
              Copy to Clipboard
            </Button>
          </Stack>

          <TextField
            select
            label="Scope to app (optional)"
            value={embedAppId}
            onChange={(e) => setEmbedAppId(e.target.value)}
            sx={{ minWidth: 280, mb: 1 }}
            size="small"
            helperText="Sessions from this embed are tagged with the selected app for Chat Operations filtering."
          >
            <MenuItem value="">None (company-wide)</MenuItem>
            {chatApps.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.appName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Embed code"
            value={widgetInstallSnippet}
            fullWidth
            multiline
            rows={4}
            margin="normal"
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
            }}
            helperText={
              embedOrigin
                ? `Public embed origin: ${embedOrigin}`
                : 'Set REACT_APP_PUBLIC_ORIGIN for a fixed embed host.'
            }
          />
        </Paper>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving || !companyId}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </PlatformErrorBoundary>
  );
}
